// One input class serving both camera modes: orbit (mouse drag + wheel) and fly
// (pointer-lock mouse-look + WASD/QE). Follows the tanks/zombies Input shape:
// keys held in a Set, edge-triggered consumeKeyPress, accumulate-then-consume deltas.

export class Input {
  private keys = new Set<string>();
  private justPressed = new Set<string>();

  // orbit drag
  private dragDX = 0;
  private dragDY = 0;
  private dragButton = -1;
  private dragging = false;
  private lastX = 0;
  private lastY = 0;

  // pointer-lock look
  private lookDX = 0;
  private lookDY = 0;
  locked = false;
  lockError = false; // set if a pointer-lock request is denied (e.g. Chrome's post-Esc cooldown)

  // base-building placement: current pointer in NDC (for world raycasts), and left-button as a
  // PLACE action instead of an orbit-drag while buildMode is on.
  buildMode = false;
  private pointerNx = 0;
  private pointerNy = 0;
  private pointerPxX = 0;
  private pointerPxY = 0;
  private placeHeldFlag = false;
  private placePressedFlag = false;
  private _ptr = { x: 0, y: 0 };

  private wheelAccum = 0;

  // Reused scratch returned by moveAxis()/consumeLook() — one persistent object each instead of a fresh
  // literal every frame. CONTRACT: callers must read the fields synchronously and never retain the reference.
  private _axis = { x: 0, z: 0 };
  private _look = { dx: 0, dy: 0 };

  // fire button (left mouse). Tracked while pointer-LOCKED (play mode) — unlike orbit-drag, which
  // is skipped when locked. `mouseDown` = held (automatics); `firePressed` = a fresh press this frame.
  mouseDown = false;
  private firePressed = false;

  constructor(private canvas: HTMLCanvasElement) {
    window.addEventListener("keydown", this.onKeyDown);
    window.addEventListener("keyup", this.onKeyUp);
    canvas.addEventListener("mousedown", this.onMouseDown);
    window.addEventListener("mouseup", this.onMouseUp);
    window.addEventListener("mousemove", this.onMouseMove);
    window.addEventListener("wheel", this.onWheel, { passive: false });
    canvas.addEventListener("contextmenu", (e) => e.preventDefault());
    document.addEventListener("pointerlockchange", this.onLockChange);
    document.addEventListener("pointerlockerror", this.onLockError);
  }

  private onKeyDown = (e: KeyboardEvent): void => {
    // don't hijack typing in the control panel inputs
    const el = e.target as HTMLElement | null;
    if (el && (el.tagName === "INPUT" || el.tagName === "SELECT" || el.tagName === "TEXTAREA")) return;
    if (e.code === "Space") e.preventDefault();
    if (!e.repeat) this.justPressed.add(e.code);
    this.keys.add(e.code);
  };
  private onKeyUp = (e: KeyboardEvent): void => {
    this.keys.delete(e.code);
  };

  private onMouseDown = (e: MouseEvent): void => {
    if (this.locked) {
      // pointer-locked = play mode: left button fires; don't do orbit-drag bookkeeping
      if (e.button === 0) {
        this.mouseDown = true;
        this.firePressed = true;
      }
      return;
    }
    // build mode: left button places (not an orbit-drag); other buttons still pan/rotate the camera
    if (this.buildMode && e.button === 0) {
      this.placeHeldFlag = true;
      this.placePressedFlag = true;
      return;
    }
    this.dragging = true;
    this.dragButton = e.button;
    this.lastX = e.clientX;
    this.lastY = e.clientY;
  };
  private onMouseUp = (e: MouseEvent): void => {
    if (e.button === 0) { this.mouseDown = false; this.placeHeldFlag = false; }
    this.dragging = false;
    this.dragButton = -1;
  };
  private onMouseMove = (e: MouseEvent): void => {
    const r = this.canvas.getBoundingClientRect();
    this.pointerNx = ((e.clientX - r.left) / Math.max(1, r.width)) * 2 - 1;
    this.pointerNy = -(((e.clientY - r.top) / Math.max(1, r.height)) * 2 - 1);
    this.pointerPxX = e.clientX; this.pointerPxY = e.clientY; // screen px, for HUD elements that follow the cursor
    if (this.locked) {
      this.lookDX += e.movementX;
      this.lookDY += e.movementY;
      return;
    }
    if (this.dragging) {
      this.dragDX += e.clientX - this.lastX;
      this.dragDY += e.clientY - this.lastY;
      this.lastX = e.clientX;
      this.lastY = e.clientY;
    }
  };
  private onWheel = (e: WheelEvent): void => {
    // The camera's wheel listener is on WINDOW and used to preventDefault() unconditionally, so it swallowed
    // the wheel over every HUD panel — scrolling the inventory zoomed the camera instead. Walk up from the
    // event target and bail if we are inside anything that can actually scroll, so UI keeps its own wheel.
    for (let n = e.target as HTMLElement | null; n && n !== document.body; n = n.parentElement) {
      if (!(n instanceof HTMLElement)) continue;
      const oy = getComputedStyle(n).overflowY;
      if ((oy === "auto" || oy === "scroll") && n.scrollHeight > n.clientHeight + 1) return;
    }
    e.preventDefault();
    this.wheelAccum += Math.sign(e.deltaY);
  };
  private onLockChange = (): void => {
    this.locked = document.pointerLockElement === this.canvas;
    if (this.locked) this.lockError = false;
  };
  private onLockError = (): void => {
    this.lockError = true;
  };

  requestFly(): void {
    this.lockError = false;
    void this.canvas.requestPointerLock();
  }
  exitFly(): void {
    if (document.pointerLockElement === this.canvas) document.exitPointerLock();
  }

  consumeDrag(): { dx: number; dy: number; button: number } {
    const o = { dx: this.dragDX, dy: this.dragDY, button: this.dragButton };
    this.dragDX = 0;
    this.dragDY = 0;
    return o;
  }
  consumeLook(): { dx: number; dy: number } {
    this._look.dx = this.lookDX;
    this._look.dy = this.lookDY;
    this.lookDX = 0;
    this.lookDY = 0;
    return this._look;
  }
  consumeWheel(): number {
    const w = this.wheelAccum;
    this.wheelAccum = 0;
    return w;
  }

  /** Current pointer position in SCREEN pixels (for HUD overlays that follow the cursor). */
  pointerPx(): { x: number; y: number } {
    return { x: this.pointerPxX, y: this.pointerPxY };
  }

  /** Current pointer position in normalized device coords (for camera raycasts). Persistent object. */
  pointerNDC(): { x: number; y: number } {
    this._ptr.x = this.pointerNx;
    this._ptr.y = this.pointerNy;
    return this._ptr;
  }
  /** Left button held this frame while in build mode (drag-draw). */
  get placeHeld(): boolean {
    return this.placeHeldFlag;
  }
  /** A fresh left-press this frame while in build mode (edge-triggered). */
  consumePlacePressed(): boolean {
    const v = this.placePressedFlag;
    this.placePressedFlag = false;
    return v;
  }

  /** WASD/arrows: x = strafe (+right), z = forward (-1 = W). */
  moveAxis(): { x: number; z: number } {
    let x = 0;
    let z = 0;
    if (this.isDown("KeyW") || this.isDown("ArrowUp")) z -= 1;
    if (this.isDown("KeyS") || this.isDown("ArrowDown")) z += 1;
    if (this.isDown("KeyA") || this.isDown("ArrowLeft")) x -= 1;
    if (this.isDown("KeyD") || this.isDown("ArrowRight")) x += 1;
    this._axis.x = x;
    this._axis.z = z;
    return this._axis;
  }
  /** Q/E vertical: +1 up, -1 down. */
  vertical(): number {
    return (this.isDown("KeyE") ? 1 : 0) - (this.isDown("KeyQ") ? 1 : 0);
  }
  isBoost(): boolean {
    return this.isDown("ShiftLeft") || this.isDown("ShiftRight");
  }

  /** A fresh left-click this frame (semi-auto / melee / deploy). Edge-triggered, cleared each frame. */
  consumeFirePressed(): boolean {
    const v = this.firePressed;
    this.firePressed = false;
    return v;
  }
  /** R held = reload request. */
  wantsReload(): boolean {
    return this.isDown("KeyR");
  }

  isDown(code: string): boolean {
    return this.keys.has(code);
  }
  consumeKeyPress(code: string): boolean {
    if (this.justPressed.has(code)) {
      this.justPressed.delete(code);
      return true;
    }
    return false;
  }
  endFrame(): void {
    this.justPressed.clear();
    this.firePressed = false; // consumed-or-dropped within one frame (matches zombies semantics)
  }
}
